// @flow
import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import { S, A, DARK } from '~/app/ui/tokens';

type Props = {
  title: string,
  classes: Object,
  actions: Node,
  children: Node,
  disableFooter: Boolean,
  disableHeader: Boolean,
  modalStyle: 'simple' | 'default',
  onClose: () => void,
  className: ?string
};

class Modal extends React.Component {
  props: Props = this.props;

  componentDidMount() {
    document.addEventListener('keyup', this.handleKeyup);
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.handleKeyup);
  }

  handleKeyup = (event: Event) => {
    if (event.key === 'Escape') {
      this.props.onClose();
    }
  }

  render() {
    const {
      className, classes, title, onClose, actions, disableFooter, disableHeader
    } = this.props;
    const header = !disableHeader && (
      <div className={classes.header}>
        <div>{title}</div>
        <button type="button" className={classes.close} onClick={onClose} aria-label="Close">
          <i className="material-icons" aria-hidden>close</i>
        </button>
      </div>
    );
    const footer = !disableFooter && (
      <div className={classes.footer}>
        {actions}
      </div>
    );
    const body = (
      <div className={classNames(classes.body, className)}>
        {this.props.children}
      </div>
    );

    return (
      <div className={classes.bg}>
        <div className={classes.container} role="dialog" aria-modal="true">
          {header}
          {body}
          {footer}
        </div>
      </div>
    );
  }
}
const modalStyles = {
  container: {
    width: {
      simple: '700px',
      medium: '60%',
      default: '80%',
      auto: 'auto'
    },
    height: {
      simple: '420px',
      medium: '95%',
      default: '80%',
      auto: 'auto'
    },
    minWidth: {
      medium: '1024px'
    }
  },
  header: {
    display: {
      simple: 'blocks',
      medium: 'flex',
      default: 'flex',
      auto: 'block'
    }
  }
};

/* =============================================================================
=    CSS
============================================================================= */
const styles = {
  bg: {
    position: 'fixed',
    top: '0',
    left: '0',
    bottom: '0',
    right: '0',
    background: 'rgba(23, 26, 32, 0.36)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 12,
    [DARK]: { background: S.scrim }
  },
  container: {
    width: props => modalStyles.container.width[props.modalStyle] || '80%',
    height: props => modalStyles.container.height[props.modalStyle] || '80%',
    minWidth: props => modalStyles.container.minWidth[props.modalStyle] || '0px',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 32px)',
    background: A.surface,
    zIndex: 13,
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'absolute',
    borderRadius: 12,
    boxShadow: A.pop,
    overflow: 'hidden',
    fontFamily: S.ui,
    color: S.fg
  },
  header: {
    flex: '0 0 52px',
    height: '52px',
    color: S.fg,
    fontSize: 16,
    fontWeight: 600,
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    padding: '0 12px 0 20px',
    background: S.chrome,
    borderBottom: `1px solid ${S.line}`
  },
  body: {
    flexGrow: '2',
    display: 'flex',
    overflow: 'auto'
  },
  close: {
    all: 'unset',
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 20 },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus
  },
  footer: {
    display: 'flex',
    flex: '0 0 56px',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderTop: `1px solid ${S.line}`
  },
  cancel: {
    fontSize: '13px',
    paddingRight: '20px',
    fontWeight: '500',
    color: S.muted,
    cursor: 'pointer',
    '&:hover': {
      color: S.fg,
    }
  }
};

export default injectSheet(styles)(Modal);
